import { SelectItem } from '@/types/ui';

export type DateItem = { year: number; quarter: number };

/**
 * Converts a flat array of {year, quarter} pairs into a TreeSelect-compatible
 * options list where years are parent nodes and quarters are leaf nodes.
 * Years are ordered newest-first; quarters within each year are ascending.
 */
export function buildYearQuarterTreeOptions(date: DateItem[]): SelectItem<string>[] {
  if (!date.length) return [];

  const yearMap = new Map<number, Set<number>>();
  for (const { year: y, quarter: q } of date) {
    const quarters = yearMap.get(y);
    if (quarters) {
      quarters.add(q);
    } else {
      yearMap.set(y, new Set([q]));
    }
  }

  const sortedYears = Array.from(yearMap.keys()).sort((a, b) => b - a);
  return sortedYears.flatMap(year => {
    const sortedQuarters = Array.from(yearMap.get(year) ?? []).sort((a, b) => a - b);
    return [
      { label: String(year), value: String(year) },
      ...sortedQuarters.map(quarter => ({
        label: `Q${quarter} ${year}`,
        value: `${year}__Q${quarter} ${year}`,
      })),
    ];
  });
}

/**
 * Converts internal {year, quarter} state into the single-element string array
 * that TreeSelect expects as its `value` prop.
 *
 * - year=2025, quarter=-1 (All)  → ["2025"]
 * - year=2025, quarter=3         → ["2025__Q3 2025"]
 * - year=null                    → []
 */
export function yearQuarterToTreeValue(year: number | null, quarter: number | null): string[] {
  if (year === null) return [];
  if (quarter === null || quarter === -1) return [String(year)];
  return [`${year}__Q${quarter} ${year}`];
}

/**
 * Parses the TreeSelect selection path back into {year, quarter}.
 * Returns null when the selection is empty or the year segment is not a number.
 *
 * - ["2025"]            → { year: 2025, quarter: -1 }
 * - ["2025__Q3 2025"]   → { year: 2025, quarter: 3  }
 * - []                  → null
 * - ["abc"]             → null
 */
export function treeValueToYearQuarter(selected: string[]): { year: number; quarter: number } | null {
  if (!selected.length) return null;
  const parts = selected[0].split('__');
  const year = parseInt(parts[0], 10);
  if (Number.isNaN(year)) return null;
  if (parts.length === 1) return { year, quarter: -1 };
  const match = parts[1].match(/^Q(\d+)/);
  if (!match) return { year, quarter: -1 };
  const quarter = parseInt(match[1], 10);
  return { year, quarter: Number.isNaN(quarter) ? -1 : quarter };
}
